import { useState } from 'react';
import {
  fetchPostApiIntroducerPortalIntroducerApplicationRejectOffers,
  PostApiIntroducerPortalIntroducerApplicationRejectOffersRequestBody,
} from '@iwoca/lapi-client/introducer_portal';
import { Button, Message, Select } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import { Offer } from 'components/SummaryCard/SummaryCard.helpers';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';

import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useReviewOffer } from '../ReviewOffer.context';
import styles from './RejectOffer.module.css';

type TRejectReasonsOption = Array<{
  id: TRejectReason;
  label: string;
}>;

type TRejectReason = Exclude<
  PostApiIntroducerPortalIntroducerApplicationRejectOffersRequestBody['data']['reason'],
  'other'
>;

const DE_REJECTION_REASONS = (): TRejectReasonsOption => [
  {
    id: 'funds_are_no_longer_required',
    label: t`Funds are no longer required`,
  },
  {
    id: 'required_a_larger_amount',
    label: t`Required a larger amount`,
  },
  {
    id: 'interest_rate_too_high',
    label: t`Interest rate too high`,
  },
  {
    id: 'received_a_better_offer',
    label: t`Received a more competitive offer`,
  },
  {
    id: 'required_a_longer_term',
    label: t`Required a longer term`,
  },
  {
    id: 'unable_to_contact_customer',
    label: t`Unable to contact client`,
  },
];

const UK_REJECTION_REASONS: TRejectReasonsOption = [
  {
    id: 'funds_are_no_longer_required',
    label: 'Funds are no longer required',
  },
  {
    id: 'required_a_larger_amount',
    label: 'Required a larger amount',
  },
  {
    id: 'interest_rate_too_high',
    label: 'Interest rate too high',
  },
  {
    id: 'received_a_better_offer',
    label: 'Received a more competitive offer',
  },
  {
    id: 'required_a_longer_term',
    label: 'Required a longer term',
  },
  {
    id: 'unable_to_contact_customer',
    label: 'Unable to contact client',
  },
];

export const RejectOffer = () => {
  const { isDE } = useLocale();
  const { trackEvent } = useAmplitude();
  const { introducerId } = useIntroducerContext();

  const [isLoading, setIsLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState<TRejectReason | null>(null);
  const [hasError, setHasError] = useState(false);

  const { setOfferDecision, selectedApplication, hasMultipleOffers } =
    useReviewOffer();
  const applicationId = selectedApplication.application_uid;

  const offerIds = selectedApplication.formal_offer_sets[0].formal_offers.map(
    (offer) => offer.offer_id,
  );

  const renderOptions = (options: { id: TRejectReason; label: string }[]) => {
    return options.map((option) => (
      <option key={option.id} value={option.id}>
        {option.label}
      </option>
    ));
  };
  const handleConfirmation = async () => {
    setHasError(false);
    setIsLoading(true);
    try {
      if (!rejectReason) {
        return;
      }

      await fetchPostApiIntroducerPortalIntroducerApplicationRejectOffers({
        introducerId: introducerId as string,
        applicationId,
        body: { data: { reason: rejectReason } },
      });

      trackEvent(AMPLITUDE_EVENTS.offerRejected, {
        rejectReason: rejectReason,
      });

      window.location.reload();
    } catch {
      setHasError(true);
    }
    setIsLoading(false);
  };

  return (
    <div className={styles.rejectContainer}>
      <h3 className={styles.heading}>
        {hasMultipleOffers ? (
          <Trans>Why does the applicant want to reject these offers?</Trans>
        ) : (
          <Trans>Why does the applicant want to reject this offer?</Trans>
        )}
      </h3>
      <p>
        {hasMultipleOffers ? (
          <Trans>
            Once you reject these offers you won’t be able to change it back.
          </Trans>
        ) : (
          <Trans>
            Once you reject this offer you won’t be able to change it back.
          </Trans>
        )}
      </p>
      <div className={styles.selectContainer}>
        <Select
          className={styles.dropdown}
          onChange={(e) => {
            setRejectReason(e.target.value as TRejectReason);
          }}
          data-testid="reject-offer-reason"
          defaultValue=""
        >
          <option disabled value="">
            {t`Reason for rejection`}
          </option>
          {renderOptions(isDE ? DE_REJECTION_REASONS() : UK_REJECTION_REASONS)}
        </Select>
        <Button
          className={styles.rejectButton}
          variant="action"
          loading={isLoading}
          disabled={!rejectReason}
          onClick={handleConfirmation}
        >
          {offerIds.length === 1
            ? t`Reject offer`
            : offerIds.length === 2
              ? t`Reject both`
              : t`Reject all`}
        </Button>
        <Button
          variant="tertiary"
          onClick={() => setOfferDecision(Offer.UNDECIDED)}
        >
          <Trans>Go back</Trans>
        </Button>
      </div>
      {hasError && <Message message="Something went wrong" type="error" />}
    </div>
  );
};
